/**
 * Automation Rules Dialog
 * Main dialog for managing automation rules with list and edit views
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { FeatureFlags } from '@/lib/feature-flags'
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { useAuth } from '@/context/auth-context'
import { useCalendars } from '@/context/calendars-context'
import { ArrowLeft, Plus, Edit, Filter, AiStarsIcon, Check, X, AlertCircle, TrashIcon, PauseIcon, PreviewIcon, PlayIcon, AutomationRemoveIcon } from '@/components/icons'
import { Loader } from '@/components/ai-elements/loader'
import { APIBroadcastProgress, type APIBroadcastProgressRef } from '@/components/APIBroadcastProgress'
import { cn } from '@/lib/utils'
import { ConditionsBuilder } from './conditions-builder'
import { CalendarList } from './calendar-list'
import { PreviewModal } from './preview-modal'
import { CalendarIntegrationsDialog } from '@/components/integrations/calendar-integrations-dialog'
import { SectionHeader } from '@/components/ui/section-header'
import { Badge } from '@/components/ui/badge'
import type {
  AutomationRuleWithAssignments,
  RuleFormData,
  GoogleCalendarColorId,
  FieldMetadata,
  OperatorMetadata,
  ConditionsGroup,
  ConditionFieldDBResponse,
  PreviewData,
  ActionTypeMetadata,
  ActionTypeDBResponse,
} from './types'
import { GOOGLE_CALENDAR_COLORS, getDefaultValueFromSchema, transformDBFieldsToMetadata, transformDBOperatorsToMetadata, transformDBActionTypesToMetadata } from './types'
import { supabase } from '@/lib/supabase'

interface AutomationRulesDialogProps {
  open: boolean
  onClose: () => void
  /** Optional callback when a rule is created (for onboarding) */
  onRuleCreated?: () => void
}

type View = 'calendar-select' | 'list' | 'edit'

export function AutomationRulesDialog({ open, onClose, onRuleCreated }: AutomationRulesDialogProps) {
  const { session, user } = useAuth()
  const { integrations, findCalendar, refreshCalendars } = useCalendars()
  const [view, setView] = useState<View>('calendar-select')
  // Selected calendar for the entire dialog flow (composite key: integration_id:calendar_id)
  const [selectedCalendar, setSelectedCalendar] = useState<string | null>(null)
  const [rules, setRules] = useState<AutomationRuleWithAssignments[]>([])
  const [loading, setLoading] = useState(true)
  const [editingRule, setEditingRule] = useState<string | null>(null)
  const [formData, setFormData] = useState<RuleFormData>(getDefaultFormData())
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null)
  const [activating, setActivating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deletingAiRules, setDeletingAiRules] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [selectedActionTypeFilters, setSelectedActionTypeFilters] = useState<string[]>([])
  // Metadata for condition fields, operators, and action types
  const [fieldMetadata, setFieldMetadata] = useState<Record<string, FieldMetadata>>({})
  const [operatorMetadata, setOperatorMetadata] = useState<Record<string, OperatorMetadata>>({})
  const [actionTypeMetadata, setActionTypeMetadata] = useState<Record<string, ActionTypeMetadata>>({})
  const [metadataLoading, setMetadataLoading] = useState(true)

  // AI Generate Rules Dialog state
  const [aiDialogOpen, setAiDialogOpen] = useState(false)
  const [aiGenerating, setAiGenerating] = useState(false)
  const [aiChannelId, setAiChannelId] = useState<string>('')
  const [aiSuccess, setAiSuccess] = useState(false)
  const [aiSuccessMessage, setAiSuccessMessage] = useState<string>('')
  const [aiHasResults, setAiHasResults] = useState(true) // false = no data, true = rules created
  const [aiCategories, setAiCategories] = useState<{ category_name: string; color_hex: string }[]>([])
  const aiProgressRef = useRef<APIBroadcastProgressRef>(null)

  // Preview Modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewData, setPreviewData] = useState<PreviewData | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // Calendar Integrations Dialog state (for empty state CTA)
  const [calendarIntegrationsDialogOpen, setCalendarIntegrationsDialogOpen] = useState(false)

  // Reset view when dialog closes
  useEffect(() => {
    if (!open) {
      setView('calendar-select')
      setSelectedCalendar(null)
      setEditingRule(null)
      setFormData(getDefaultFormData())
      setSelectedActionTypeFilters([])
      setFilterOpen(false)
      // Reset AI dialog state
      setAiDialogOpen(false)
      setAiGenerating(false)
      setAiChannelId('')
      setAiSuccess(false)
      setAiSuccessMessage('')
      setAiHasResults(true)
      setAiCategories([])
    }
  }, [open])


  const fetchRules = useCallback(async () => {
    if (!session?.access_token) return

    try {
      setLoading(true)

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/automation-rules-list`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to fetch rules')
      }

      const data = await response.json()
      setRules(data.rules || [])
    } catch (error) {
      console.error('Error fetching rules:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to load rules')
    } finally {
      setLoading(false)
    }
  }, [session?.access_token])

  // Fetch condition field metadata and action type metadata from Supabase
  const fetchMetadata = useCallback(async () => {
    try {
      setMetadataLoading(true)

      // Fetch condition fields and action types in parallel
      const [conditionsResult, actionTypesResult] = await Promise.all([
        supabase
          .from('automation_condition_fields')
          .select('*')
          .order('field_label', { ascending: true }),
        supabase
          .from('automation_action_types')
          .select('*')
          .eq('is_enabled', true)
          .order('display_order', { ascending: true })
      ])

      if (conditionsResult.error) {
        console.error('Error fetching condition fields metadata:', conditionsResult.error)
        toast.error('Failed to load condition fields')
        return
      }

      if (conditionsResult.data && conditionsResult.data.length > 0) {
        const dbFields = conditionsResult.data as ConditionFieldDBResponse[]
        const fields = transformDBFieldsToMetadata(dbFields)
        const operators = transformDBOperatorsToMetadata(dbFields)
        
        setFieldMetadata(fields)
        setOperatorMetadata(operators)
      }

      if (actionTypesResult.data && actionTypesResult.data.length > 0) {
        const dbActionTypes = actionTypesResult.data as ActionTypeDBResponse[]
        setActionTypeMetadata(transformDBActionTypesToMetadata(dbActionTypes))
      }
    } catch (error) {
      console.error('Error fetching metadata:', error)
      toast.error('Failed to load condition fields')
    } finally {
      setMetadataLoading(false)
    }
  }, [])

  // Fetch rules and metadata on mount
  // Calendars are fetched globally via CalendarsContext
  useEffect(() => {
    if (open) {
      fetchRules()
      fetchMetadata()
    }
  }, [open, fetchRules, fetchMetadata])

  const handleAddRule = () => {
    setEditingRule(null)
    // Auto-populate calendar_id from the pre-selected calendar
    setFormData({
      ...getDefaultFormData(),
      calendar_id: selectedCalendar,
    })
    setView('edit')
  }


  const handleCloseAiDialog = () => {
    // Cleanup progress ref if active
    if (aiProgressRef.current) {
      aiProgressRef.current.forceCleanup()
    }
    setAiDialogOpen(false)
    setAiGenerating(false)
    setAiChannelId('')
    setAiSuccess(false)
    setAiSuccessMessage('')
    setAiHasResults(true)
    setAiCategories([])
    // Refetch rules and calendars in case new draft rules were created (fire and forget)
    fetchRules()
    refreshCalendars()
  }

  const handleGenerateAiRules = async () => {
    if (!session?.access_token || !selectedCalendar) {
      toast.error('No calendar selected')
      return
    }

    // Parse composite key (integration_id:calendar_id)
    const parts = selectedCalendar.split(':')
    if (parts.length < 2) {
      toast.error('Invalid calendar selection')
      return
    }
    const integrationId = parts[0]
    const calendarId = parts.slice(1).join(':') // Rejoin in case calendar_id contains ':'

    try {
      setAiGenerating(true)
      setAiSuccess(false)
      setAiSuccessMessage('')
      setAiHasResults(true)
      setAiCategories([])

      // Cleanup existing progress component if any (for retry scenarios)
      if (aiProgressRef.current) {
        aiProgressRef.current.forceCleanup()
      }
      
      // Reset channel to unmount old component, then set new one
      setAiChannelId('')
      
      // Generate unique channel ID (use setTimeout to ensure state update completes)
      await new Promise(resolve => setTimeout(resolve, 50))
      const channelId = `${user?.id}-${Date.now()}`
      setAiChannelId(channelId)

      const response = await fetch('https://n8n.calintent.com/webhook/api/v4/automation-rules/generate-ai', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          integrationId,
          calendarId,
          channel: channelId,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || errorData.error || 'Failed to generate rules')
      }

      const result = await response.json()
      
      // Check for success and result value
      if (result.success || result.message) {
        setAiSuccess(true)
        
        // Check if result is 0 (no data) or has actual results
        if (result.result === 0) {
          setAiHasResults(false)
          setAiSuccessMessage(result.message || 'Not enough event data to generate rules. Try adding more color-coded events to your calendar.')
          setAiCategories([])
        } else {
          setAiHasResults(true)
          setAiSuccessMessage(result.message || 'Rules generated successfully!')
          // Store categories if present
          setAiCategories(result.categories || [])
          // Call onRuleCreated callback for onboarding
          onRuleCreated?.()
        }
        
        // Don't trigger completion - let broadcast messages control the visual state
        // The APIBroadcastProgress component will show progress/error/complete based on broadcast events only
      }
    } catch (error) {
      console.error('Error generating AI rules:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to generate rules')
      // Don't trigger completion on error - broadcast messages control the visual state
    } finally {
      setAiGenerating(false)
    }
  }

  const handleEditRule = async (rule: AutomationRuleWithAssignments) => {
    setEditingRule(rule.id)
    
    // Convert calendar to composite key (integration_id:calendar_id)
    let compositeCalendarId: string | null = null
    
    // Handle AI-generated rules with NULL calendar_id
    if (rule.is_ai_generated && !rule.calendar_id && rule.integration_id_ref && rule.calendar_id_external_ref) {
      // Use reference columns for AI-generated rules that haven't been activated yet
      compositeCalendarId = `${rule.integration_id_ref}:${rule.calendar_id_external_ref}`
    } else if (rule.calendar) {
      // Handle regular rules with calendar assignment
      if (rule.calendar.integration_id && rule.calendar.calendar_id) {
        compositeCalendarId = `${rule.calendar.integration_id}:${rule.calendar.calendar_id}`
      } else if (integrations.length > 0 && rule.calendar.calendar_id) {
        // Fallback: try to match from integrations (legacy data without integration_id)
        for (const integration of integrations) {
          const matchingCalendar = integration.calendars.find(cal => cal.id === rule.calendar?.calendar_id)
          if (matchingCalendar) {
            compositeCalendarId = `${integration.id}:${matchingCalendar.id}`
            break
          }
        }
      }
    }

    setFormData({
      rule_name: rule.rule_name,
      conditions: normalizeConditionsFromDB(rule.conditions),
      action: rule.action,
      calendar_id: compositeCalendarId,
      priority: rule.priority,
      is_active: rule.is_active,
    })
    setView('edit')
  }

  const handleDeleteRule = (ruleId: string) => {
    setRuleToDelete(ruleId)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = async () => {
    if (!ruleToDelete || !session?.access_token) return

    try {
      setDeleting(true)
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/automation-rules-delete`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ rule_id: ruleToDelete }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to delete rule')
      }

      toast.success('Rule deleted successfully')
      await fetchRules()
      await refreshCalendars() // Refresh calendar data as automation status may have changed
      setDeleteConfirmOpen(false)
      setRuleToDelete(null)

      if (view === 'edit' && editingRule === ruleToDelete) {
        setView('list')
      }
    } catch (error) {
      console.error('Error deleting rule:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to delete rule')
    } finally {
      setDeleting(false)
    }
  }

  const validateActionValue = (): string | null => {
    const action = formData.action
    if (!action?.value) return 'Please configure the action'

    const schema = actionTypeMetadata[action.type]?.valueSchema
    if (!schema) return 'Please configure the action'

    if (schema.type === 'form' && schema.fields) {
      for (const field of schema.fields) {
        if (field.type === 'text') {
          const objValue = action.value as Record<string, unknown>
          const fieldVal = objValue[field.name]
          if (!fieldVal || (typeof fieldVal === 'string' && fieldVal.trim() === '')) {
            return `Please enter a value for "${field.label || field.name}"`
          }
        }
      }
      return null
    }

    if (typeof action.value === 'string' && action.value.trim() === '') {
      const label = actionTypeMetadata[action.type]?.displayName || 'action'
      return `Please select a value for the ${label}`
    }

    return null
  }

  const handleSaveRule = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!session?.access_token) {
      toast.error('Authentication required')
      return
    }

    if (!formData.rule_name.trim()) {
      toast.error('Please enter a rule name')
      return
    }

    if (!formData.calendar_id) {
      toast.error('Please select a calendar')
      return
    }

    if (formData.conditions.conditions.length === 0) {
      toast.error('Please add at least one condition')
      return
    }

    for (const condition of formData.conditions.conditions) {
      const opMeta = operatorMetadata[condition.operator]
      if (opMeta?.inputType === 'none') continue
      if (!condition.values || condition.values.length === 0 || !condition.values.some(v => v.trim() !== '')) {
        const fieldMeta = fieldMetadata[condition.field]
        toast.error(`Please enter a value for the "${fieldMeta?.label || condition.field}" condition`)
        return
      }
    }

    const actionError = validateActionValue()
    if (actionError) {
      toast.error(actionError)
      return
    }

    try {
      setSaving(true)
      
      const endpoint = editingRule
        ? 'automation-rules-update'
        : 'automation-rules-create'

      // Parse composite key (integration_id:calendar_id) and send separately
      if (!formData.calendar_id || !formData.calendar_id.includes(':')) {
        throw new Error('Invalid calendar selection')
      }

      const parts = formData.calendar_id.split(':')
      if (parts.length < 2) {
        throw new Error('Invalid calendar selection')
      }

      const integration_id = parts[0]
      const calendar_id_external = parts.slice(1).join(':') // Rejoin in case calendar_id contains ':'

      if (!integration_id || !calendar_id_external) {
        throw new Error('Invalid calendar selection')
      }

      // Build request body with separate fields
      // Note: Calendar fields are only sent for new rules, not updates (calendar cannot be changed after creation)
      const body = editingRule
        ? { 
            rule_id: editingRule, 
            rule_name: formData.rule_name,
            conditions: formData.conditions,
            action: formData.action,
            priority: formData.priority,
            is_active: formData.is_active,
          }
        : {
            rule_name: formData.rule_name,
            conditions: formData.conditions,
            action: formData.action,
            priority: formData.priority,
            is_active: formData.is_active,
            integration_id,
            calendar_id_external,
          }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${endpoint}`,
        {
          method: editingRule ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to ${editingRule ? 'update' : 'create'} rule`)
      }

      toast.success(`Rule ${editingRule ? 'updated' : 'created'} successfully`)
      await fetchRules()
      await refreshCalendars() // Refresh calendar data as automation status may have changed
      setPreviewModalOpen(false)  // Close preview modal if open
      setView('list')
      setEditingRule(null)
      setFormData(getDefaultFormData())
      
      // Call onRuleCreated callback for onboarding (only on create, not update)
      if (!editingRule) {
        onRuleCreated?.()
      }
    } catch (error) {
      console.error('Error saving rule:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to save rule')
    } finally {
      setSaving(false)
    }
  }

  const handlePauseRule = async (ruleId: string) => {
    if (!session?.access_token || !ruleId) return

    try {
      setSaving(true)

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/automation-rules-deactivate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rule_id: ruleId,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to pause rule')
      }

      toast.success('Rule paused successfully')
      await fetchRules()
      await refreshCalendars() // Refresh calendar data as automation status may have changed
      // Go back to list view (same behavior as Save)
      setView('list')
      setEditingRule(null)
      setFormData(getDefaultFormData())
    } catch (error) {
      console.error('Error pausing rule:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to pause rule')
    } finally {
      setSaving(false)
    }
  }

  const handleReactivateRule = async (ruleId: string) => {
    if (!session?.access_token || !ruleId) return

    // Validate form data before reactivating
    if (!formData.rule_name.trim()) {
      toast.error('Please enter a rule name')
      return
    }

    if (formData.conditions.conditions.length === 0) {
      toast.error('Please add at least one condition')
      return
    }

    for (const condition of formData.conditions.conditions) {
      const opMeta = operatorMetadata[condition.operator]
      if (opMeta?.inputType === 'none') continue
      if (!condition.values || condition.values.length === 0 || !condition.values.some(v => v.trim() !== '')) {
        const fieldMeta = fieldMetadata[condition.field]
        toast.error(`Please enter a value for the "${fieldMeta?.label || condition.field}" condition`)
        return
      }
    }

    const actionError = validateActionValue()
    if (actionError) {
      toast.error(actionError)
      return
    }

    try {
      setSaving(true)

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/automation-rules-reactivate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rule_id: ruleId,
            rule_name: formData.rule_name,
            conditions: formData.conditions,
            action: formData.action,
            priority: formData.priority,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to reactivate rule')
      }

      toast.success('Rule reactivated successfully')
      await fetchRules()
      await refreshCalendars() // Refresh calendar data as automation status may have changed
      // Go back to list view (same behavior as Save)
      setView('list')
      setEditingRule(null)
      setFormData(getDefaultFormData())
    } catch (error) {
      console.error('Error reactivating rule:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to reactivate rule')
    } finally {
      setSaving(false)
    }
  }

  const handlePreview = async () => {
    if (!session?.access_token || !formData.calendar_id) {
      toast.error('Please select a calendar')
      return
    }

    // Parse composite key (integration_id:calendar_id)
    const parts = formData.calendar_id.split(':')
    if (parts.length < 2) {
      toast.error('Invalid calendar selection')
      return
    }
    const integration_id = parts[0]
    const calendar_id_external = parts.slice(1).join(':') // Rejoin in case calendar_id contains ':'

    // Validate that at least one condition exists
    if (formData.conditions.conditions.length === 0) {
      toast.error('Please add at least one condition')
      return
    }

    for (const condition of formData.conditions.conditions) {
      const opMeta = operatorMetadata[condition.operator]
      if (opMeta?.inputType === 'none') continue
      if (!condition.values || condition.values.length === 0 || !condition.values.some(v => v.trim() !== '')) {
        const fieldMeta = fieldMetadata[condition.field]
        toast.error(`Please enter a value for the "${fieldMeta?.label || condition.field}" condition`)
        return
      }
    }

    const actionError = validateActionValue()
    if (actionError) {
      toast.error(actionError)
      return
    }

    try {
      setPreviewLoading(true)
      setPreviewError(null)
      setPreviewData(null)
      setPreviewModalOpen(true)

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/automation-rules-preview`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rule_name: formData.rule_name,
            conditions: formData.conditions,
            action: formData.action,
            priority: formData.priority,
            integration_id,
            calendar_id_external,
            existing_rule_id: editingRule || undefined,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to generate preview')
      }

      const result = await response.json()
      
      if (result.success && result.preview) {
        setPreviewData(result.preview)
      } else {
        throw new Error(result.error || 'Invalid preview response')
      }
    } catch (error) {
      console.error('Error generating preview:', error)
      setPreviewError(error instanceof Error ? error.message : 'Failed to generate preview')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleActivateRules = async () => {
    if (!session?.access_token) return

    try {
      setActivating(true)

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/automation-rules-activate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to activate rules')
      }

      const data = await response.json()
      const count = data.activated_calendars?.length || 0
      toast.success(`Rules activated for ${count} ${count === 1 ? 'calendar' : 'calendars'}`)
      await fetchRules()
      await refreshCalendars() // Refresh calendar data as automation status may have changed
    } catch (error) {
      console.error('Error activating rules:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to activate rules')
    } finally {
      setActivating(false)
    }
  }

  const handleDeleteInactiveAiRules = async () => {
    if (!user?.id) return

    try {
      setDeletingAiRules(true)

      // Delete all inactive AI-generated rules directly via Supabase
      const { error, count } = await supabase
        .from('automation_rules')
        .delete({ count: 'exact' })
        .eq('user_id', user.id)
        .eq('is_active', false)
        .eq('is_ai_generated', true)

      if (error) {
        throw error
      }

      const deletedCount = count || 0
      toast.success(`Deleted ${deletedCount} inactive AI-generated ${deletedCount === 1 ? 'rule' : 'rules'}`)
      await fetchRules()
      await refreshCalendars() // Refresh calendar data as automation status may have changed
    } catch (error) {
      console.error('Error deleting inactive AI rules:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to delete AI rules')
    } finally {
      setDeletingAiRules(false)
    }
  }

  // Filter rules based on selected calendar and filters
  const filteredRules = rules.filter(rule => {
    // Filter by selected calendar (main flow filter)
    if (selectedCalendar) {
      const [selectedIntegrationId, ...selectedCalendarIdParts] = selectedCalendar.split(':')
      const selectedCalendarId = selectedCalendarIdParts.join(':')
      
      // Check for regular rules with calendar assignment
      if (rule.calendar?.integration_id && rule.calendar?.calendar_id) {
        if (rule.calendar.integration_id !== selectedIntegrationId || 
            rule.calendar.calendar_id !== selectedCalendarId) {
          return false
        }
      } 
      // Check for AI-generated rules with ref fields
      else if (rule.is_ai_generated && rule.integration_id_ref && rule.calendar_id_external_ref) {
        if (rule.integration_id_ref !== selectedIntegrationId || 
            rule.calendar_id_external_ref !== selectedCalendarId) {
          return false
        }
      } else {
        // Rule doesn't match the selected calendar
        return false
      }
    }

    // Filter by action type
    if (selectedActionTypeFilters.length > 0) {
      if (!selectedActionTypeFilters.includes(rule.action.type)) return false
    }

    return true
  })

  // Show activate button if there are active rules with inactive calendars
  const hasInactiveCalendars = filteredRules.some(rule => 
    rule.is_active && 
    rule.calendar &&
    !rule.calendar.automation_is_active
  )

  // Show delete AI rules button if there are inactive AI-generated rules
  const hasInactiveAiRules = filteredRules.some(rule => 
    !rule.is_active && 
    rule.is_ai_generated
  )

  const hasActiveFilters = selectedActionTypeFilters.length > 0

  // Get the selected calendar name for display in headers
  const selectedCalendarName = useMemo(() => {
    if (!selectedCalendar) return null
    const [integrationId, ...calendarIdParts] = selectedCalendar.split(':')
    const calendarId = calendarIdParts.join(':')
    return findCalendar(integrationId, calendarId)?.summary || null
  }, [selectedCalendar, findCalendar])

  // Resolve the full CalendarResource for the selected calendar (for status flags)
  const selectedCalendarResource = useMemo(() => {
    if (!selectedCalendar) return null
    const [integrationId, ...calendarIdParts] = selectedCalendar.split(':')
    const calendarId = calendarIdParts.join(':')
    return findCalendar(integrationId, calendarId) || null
  }, [selectedCalendar, findCalendar])

  // Feature flag for AI color coding
  const hasAiColorCoding = useFeatureFlagEnabled(FeatureFlags.DEV_FEATURES_ACCESS) === true

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent
          className="
            max-w-none
            p-0
            border-0 shadow-none
            w-full h-[90vh] max-h-[90vh]
            md:w-[800px] md:h-[70vh] md:max-h-[70vh]
            flex flex-col
            fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
            md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
            rounded-t-3xl md:rounded-lg
          "
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          hideClose
        >
          <DialogTitle className="sr-only">Automation Rules</DialogTitle>
          <DialogDescription className="sr-only">
            {view === 'list' 
              ? 'Manage your automation rules for calendar events' 
              : (editingRule ? 'Edit automation rule' : 'Create new automation rule')}
          </DialogDescription>

          {/* Header */}
          <DialogHeader className="px-2 pt-6 pb-4 flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="secondary"
                onClick={() => {
                  if (view === 'calendar-select') {
                    onClose()
                  } else if (view === 'list') {
                    setView('calendar-select')
                  } else {
                    setView('list')
                  }
                }}
                className="flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden md:inline">Back</span>
              </Button>

              <div className="flex-1 text-center">
                <h2 className="font-semibold text-lg">
                  {view === 'calendar-select' 
                    ? 'Automation rules' 
                    : view === 'list' 
                      ? 'Automation Rules' 
                      : (editingRule ? 'Edit Rule' : 'Add Rule')}
                </h2>
                {(view === 'list' || view === 'edit') && selectedCalendarName && (
                  <p className="text-sm text-muted-foreground truncate">
                    {selectedCalendarName}
                  </p>
                )}
              </div>

              {view === 'list' && (
                <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="secondary"
                      className={cn(
                        "flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full",
                        hasActiveFilters && "ring-2 ring-primary"
                      )}
                    >
                      <Filter className="h-4 w-4" />
                      <span className="hidden md:inline">Filter</span>
                      {hasActiveFilters && (
                        <span className="ml-1 flex h-2 w-2 rounded-full bg-primary" />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <FilterPopoverContent
                      selectedActionTypeFilters={selectedActionTypeFilters}
                      onActionTypeFilterChange={setSelectedActionTypeFilters}
                      onClearFilters={() => {
                        setSelectedActionTypeFilters([])
                      }}
                      hasActiveFilters={hasActiveFilters}
                      actionTypeMetadata={actionTypeMetadata}
                    />
                  </PopoverContent>
                </Popover>
              )}

              {(view === 'edit' || view === 'calendar-select') && (
                <div className="w-[68px] md:w-[76px]"></div>
              )}
            </div>
          </DialogHeader>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            {view === 'calendar-select' && (
              <div className="px-2 pb-6">
                <SectionHeader 
                  title="Calendars"
                  description="Select a calendar to manage its automation rules."
                />
                <CalendarList
                  selectedCalendarId={selectedCalendar}
                  onChange={(calendarId) => {
                    if (calendarId) {
                      setSelectedCalendar(calendarId)
                      setView('list')
                    }
                  }}
                  isSelectable={true}
                  onConnectClick={() => setCalendarIntegrationsDialogOpen(true)}
                />
              </div>
            )}

            {view === 'list' && (
              <>
                {hasAiColorCoding && selectedCalendarResource?.integrationCalendarId && (
                  <AiColorCodingSettings
                    integrationCalendarId={selectedCalendarResource.integrationCalendarId}
                    analyticsIsActive={selectedCalendarResource.analyticsIsActive ?? false}
                    aiColorCodingIsActive={selectedCalendarResource.aiColorCodingIsActive ?? false}
                    onToggle={async (enabled) => {
                      const { error } = await supabase
                        .from('integrations_calendars')
                        .update({ ai_color_coding_is_active: enabled })
                        .eq('id', selectedCalendarResource.integrationCalendarId!)
                      if (error) {
                        toast.error('Failed to update AI color coding setting')
                        return
                      }
                      await refreshCalendars()
                    }}
                  />
                )}
                <RulesList
                  rules={filteredRules}
                  loading={loading}
                  onEdit={handleEditRule}
                  hasActiveFilters={hasActiveFilters}
                  actionTypeMetadata={actionTypeMetadata}
                />
              </>
            )}

            {view === 'edit' && (
              <RuleForm
                formData={formData}
                onChange={setFormData}
                onSubmit={handleSaveRule}
                onDelete={editingRule ? () => handleDeleteRule(editingRule) : undefined}
                onPause={editingRule && formData.is_active ? () => handlePauseRule(editingRule) : undefined}
                onReactivate={editingRule && !formData.is_active ? () => handleReactivateRule(editingRule) : undefined}
                onPreview={handlePreview}
                previewLoading={previewLoading}
                isEditing={!!editingRule}
                editingRuleId={editingRule}
                rules={rules}
                saving={saving}
                fieldMetadata={fieldMetadata}
                operatorMetadata={operatorMetadata}
                actionTypeMetadata={actionTypeMetadata}
                metadataLoading={metadataLoading}
              />
            )}
          </div>

          {/* Footer - Only show in list view (not calendar-select or edit) */}
          {view === 'list' && selectedCalendar && (
            <div className="px-6 py-4 flex-shrink-0 space-y-3">
              {/* Action buttons row */}
              {(hasInactiveCalendars || hasInactiveAiRules) && (
                <div className="flex items-center gap-3">
                  {hasInactiveCalendars && (
                    <Button
                      onClick={handleActivateRules}
                      disabled={activating}
                      variant="secondary"
                      className="flex-1"
                      size="lg"
                    >
                      {activating ? (
                        <>
                          <Loader size={16} className="mr-2" />
                          Activating...
                        </>
                      ) : (
                        'Activate Rules'
                      )}
                    </Button>
                  )}
                  {hasInactiveAiRules && (
                    <Button
                      onClick={handleDeleteInactiveAiRules}
                      disabled={deletingAiRules}
                      variant="outline"
                      className="flex-1 border-destructive text-destructive hover:bg-destructive/10"
                      size="lg"
                    >
                      {deletingAiRules ? (
                        <>
                          <Loader size={16} className="mr-2" />
                          Deleting...
                        </>
                      ) : (
                        'Delete Inactive AI Rules'
                      )}
                    </Button>
                  )}
                </div>
              )}
              
              {/* Primary buttons row */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleAddRule}
                  className="flex items-center gap-2 flex-1"
                  size="lg"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add rule</span>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md" hideClose>
          <DialogHeader>
            <DialogTitle>Delete Rule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this rule? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false)
                setRuleToDelete(null)
              }}
              className="w-32"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
              className="w-32"
            >
              {deleting ? (
                <>
                  <Loader size={16} className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generate Rules Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={(isOpen) => !isOpen && handleCloseAiDialog()}>
        <DialogContent
          className="
            max-w-none
            p-0
            border-0 shadow-none
            w-full h-[90vh] max-h-[90vh]
            md:w-[600px] md:h-auto md:max-h-[70vh]
            flex flex-col
            fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
            md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
            rounded-t-3xl md:rounded-lg
          "
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          hideClose
        >
          <DialogTitle className="sr-only">Generate Rules with AI</DialogTitle>
          <DialogDescription className="sr-only">
            Use AI to generate automation rules based on your color-coded calendar events
          </DialogDescription>

          {/* Header */}
          <DialogHeader className="px-2 pt-6 pb-4 flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleCloseAiDialog()
                }}
                className="flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden md:inline">Back</span>
              </Button>

              <h2 className="flex-1 text-center font-semibold text-lg">
                Draft with AI
              </h2>

              <div className="w-[68px] md:w-[76px]"></div>
            </div>
          </DialogHeader>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6">
            {aiSuccess ? (
              // Success/No Data State
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className={cn(
                  "w-16 h-16 rounded-full flex items-center justify-center",
                  aiHasResults ? "bg-primary/10" : "bg-destructive/10"
                )}>
                  {aiHasResults ? (
                    <Check className="h-8 w-8 text-primary" />
                  ) : (
                    <X className="h-8 w-8 text-destructive" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-center">
                  {aiHasResults ? 'Rules Generated!' : 'Not Enough Data'}
                </h3>
                <p className="text-sm text-muted-foreground text-center max-w-sm">
                  {aiSuccessMessage || (aiHasResults 
                    ? 'Your AI-generated draft rules have been created. They are inactive by default - review and activate them when ready.'
                    : 'Not enough event data to generate rules. Try adding more color-coded events to your calendar.'
                  )}
                </p>

                {/* Categories List - Only show if we have results and categories */}
                {aiHasResults && aiCategories.length > 0 && (
                  <div className="w-full max-w-sm space-y-2 mt-4">
                    <h4 className="text-sm font-semibold text-center text-muted-foreground">
                      Identified Categories
                    </h4>
                    <div className="space-y-2">
                      {aiCategories.map((category, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 px-4 py-2 rounded-lg bg-muted/50"
                        >
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0 border border-border"
                            style={{ backgroundColor: category.color_hex }}
                          />
                          <span className="text-sm text-foreground">
                            {category.category_name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // AI Generation Info
              <div className="space-y-4 pb-4">
                <p className="text-sm text-muted-foreground">
                  Identify patterns in color coded events from the last 90 days and create draft automation rules (inactive rules that you will manually need to unpause)
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 flex-shrink-0 space-y-3">
            {/* API Broadcast Progress - Keep visible on result screen */}
            {aiChannelId && (
              <APIBroadcastProgress
                ref={aiProgressRef}
                channelId={aiChannelId}
                onComplete={() => {
                  // Progress component auto-removes after timeout
                }}
                className="w-full max-w-none"
              />
            )}

            {!aiSuccess && (
              <Button
                onClick={handleGenerateAiRules}
                disabled={aiGenerating}
                className="w-full"
                size="lg"
              >
                {aiGenerating ? (
                  <>
                    <Loader size={16} className="mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <AiStarsIcon className="h-4 w-4 mr-2" />
                    Generate rules
                  </>
                )}
              </Button>
            )}

            {aiSuccess && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleCloseAiDialog()
                }}
                className="w-full"
                size="lg"
              >
                Done
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <PreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        loading={previewLoading}
        data={previewData}
        error={previewError}
        onRetry={handlePreview}
        isCreateMode={!editingRule}
        onCreateRule={() => {
          // Create a synthetic form event to reuse handleSaveRule
          const syntheticEvent = { preventDefault: () => {} } as React.FormEvent
          handleSaveRule(syntheticEvent)
          // Modal will be closed by handleSaveRule on success
        }}
        creating={saving}
      />

      {/* Calendar Integrations Dialog (for empty state CTA) */}
      <CalendarIntegrationsDialog
        open={calendarIntegrationsDialogOpen}
        onClose={() => setCalendarIntegrationsDialogOpen(false)}
      />
    </>
  )
}

// AI Color Coding General Settings Card
interface AiColorCodingSettingsProps {
  integrationCalendarId: string
  analyticsIsActive: boolean
  aiColorCodingIsActive: boolean
  onToggle: (enabled: boolean) => Promise<void>
}

function AiColorCodingSettings({ analyticsIsActive, aiColorCodingIsActive, onToggle }: AiColorCodingSettingsProps) {
  const [toggling, setToggling] = useState(false)

  const handleToggle = async (checked: boolean) => {
    setToggling(true)
    try {
      await onToggle(checked)
    } finally {
      setToggling(false)
    }
  }

  return (
    <div className="px-2 pb-2">
      <SectionHeader title="General settings" />
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <Label className="font-medium">AI color coding</Label>
            <Switch
              checked={aiColorCodingIsActive}
              onCheckedChange={handleToggle}
              disabled={!analyticsIsActive || toggling}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {!analyticsIsActive
              ? 'Enable analytics for this calendar to use AI color coding.'
              : 'When analytics are enabled for this calendar and life domains and labels color-mapped, if no automation color coding rule matched an event, we will use AI to update the event\'s color.'}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

// Rules List View Component
interface RulesListProps {
  rules: AutomationRuleWithAssignments[]
  loading: boolean
  onEdit: (rule: AutomationRuleWithAssignments) => void
  hasActiveFilters?: boolean
  actionTypeMetadata: Record<string, ActionTypeMetadata>
}

function RulesList({ rules, loading, onEdit, hasActiveFilters, actionTypeMetadata }: RulesListProps) {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-3">
        <Loader size={24} className="text-muted-foreground" />
        <div className="text-muted-foreground">Loading rules...</div>
      </div>
    )
  }

  // Split rules into sections
  const activeRules = rules.filter(r => r.is_active)
  const inactiveRules = rules.filter(r => !r.is_active)

  if (rules.length === 0) {
    return (
      <div className="px-6 py-12">
        <div className="flex flex-col items-center text-center text-muted-foreground">
          {hasActiveFilters ? (
            <>
              <p className="mb-2">No rules match your filters</p>
              <p className="text-sm">Try adjusting your filter criteria</p>
            </>
          ) : (
            <>
              <AutomationRemoveIcon className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="mb-2">No automation rules yet</p>
              <p className="text-sm">Create your first rule to automate calendar event colors</p>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="px-2 pb-6 space-y-6">
      {/* Active Rules Section */}
      {activeRules.length > 0 && (
        <div>
          <SectionHeader title={`Active Rules (${activeRules.length})`} />
          <Card className="py-0">
            <CardContent className="p-0">
              {activeRules.map((rule, index) => (
                <RuleItem
                  key={rule.id}
                  rule={rule}
                  onEdit={onEdit}
                  showBorder={index !== activeRules.length - 1}
                  actionTypeMetadata={actionTypeMetadata}
                />
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Inactive Rules Section */}
      {inactiveRules.length > 0 && (
        <div>
          <SectionHeader title={`Inactive Rules (${inactiveRules.length})`} />
          <Card className="py-0">
            <CardContent className="p-0">
              {inactiveRules.map((rule, index) => (
                <RuleItem
                  key={rule.id}
                  rule={rule}
                  onEdit={onEdit}
                  showBorder={index !== inactiveRules.length - 1}
                  actionTypeMetadata={actionTypeMetadata}
                />
              ))}
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  )
}

// Individual Rule Item Component
interface RuleItemProps {
  rule: AutomationRuleWithAssignments
  onEdit: (rule: AutomationRuleWithAssignments) => void
  showBorder: boolean
  actionTypeMetadata: Record<string, ActionTypeMetadata>
}

function RuleItem({ rule, onEdit, showBorder, actionTypeMetadata }: RuleItemProps) {
  const actionDisplayName = actionTypeMetadata[rule.action.type]?.displayName ?? rule.action.type
  const showColorSwatch =
    rule.action.type === 'update_color' &&
    typeof rule.action.value === 'string' &&
    rule.action.value in GOOGLE_CALENDAR_COLORS
  return (
    <div
      className={cn(
        'p-4 flex items-center justify-between gap-4',
        showBorder && 'border-b border-border'
      )}
    >
      {/* Left: priority number */}
      <div className="w-8 flex-shrink-0 flex items-start justify-center">
        <span className="text-sm text-muted-foreground tabular-nums">{rule.priority}</span>
      </div>

      {/* Right: title + tag line */}
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-medium truncate">{rule.rule_name}</h3>
          {rule.is_ai_generated && (
            <span className="text-xs px-2 py-0.5 rounded-md bg-primary/10 text-primary flex items-center gap-1">
              <AiStarsIcon className="h-3 w-3" />
              AI
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-xs font-normal text-muted-foreground">
            {actionDisplayName}
          </Badge>
          {showColorSwatch && (
            <div className="flex items-center gap-1">
              <div
                className="w-3 h-3 rounded-full border border-border"
                style={{
                  backgroundColor: GOOGLE_CALENDAR_COLORS[rule.action.value as GoogleCalendarColorId]?.hex
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(rule)}
        >
          <Edit className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// Rule Form Component
interface RuleFormProps {
  formData: RuleFormData
  onChange: (data: RuleFormData) => void
  onSubmit: (e: React.FormEvent) => void
  onDelete?: () => void
  onPause?: () => void
  onReactivate?: () => void
  onPreview?: () => void
  previewLoading?: boolean
  isEditing: boolean
  editingRuleId: string | null
  rules: AutomationRuleWithAssignments[]
  saving: boolean
  fieldMetadata: Record<string, FieldMetadata>
  operatorMetadata: Record<string, OperatorMetadata>
  actionTypeMetadata: Record<string, ActionTypeMetadata>
  metadataLoading: boolean
}

function RuleForm({ formData, onChange, onSubmit, onDelete, onPause, onReactivate, onPreview, previewLoading, isEditing, editingRuleId, rules, saving, fieldMetadata, operatorMetadata, actionTypeMetadata, metadataLoading }: RuleFormProps) {
  // Get the current rule for AI notes (only available when editing)
  const currentRule = isEditing && editingRuleId ? rules.find(r => r.id === editingRuleId) : null
  const showAiNotes = currentRule?.is_ai_generated && !currentRule?.is_active && currentRule?.ai_notes

  // Check for priority conflicts with other rules on the same calendar
  const conflictingRules = useMemo(() => {
    if (!formData.calendar_id) return []
    
    // Parse composite key (integration_id:calendar_id)
    const parts = formData.calendar_id.split(':')
    if (parts.length < 2) return []
    
    const currentIntegrationId = parts[0]
    const currentCalendarId = parts.slice(1).join(':')
    
    return rules.filter(rule => {
      // Skip the rule being edited
      if (editingRuleId && rule.id === editingRuleId) return false
      
      // Check if priority matches
      if (rule.priority !== formData.priority) return false
      
      // Check if calendar matches
      // For regular rules with calendar assignment
      if (rule.calendar?.integration_id && rule.calendar?.calendar_id) {
        return rule.calendar.integration_id === currentIntegrationId && 
               rule.calendar.calendar_id === currentCalendarId
      }
      
      // For AI-generated rules with ref fields
      if (rule.is_ai_generated && rule.integration_id_ref && rule.calendar_id_external_ref) {
        return rule.integration_id_ref === currentIntegrationId && 
               rule.calendar_id_external_ref === currentCalendarId
      }
      
      return false
    })
  }, [rules, formData.calendar_id, formData.priority, editingRuleId])

  const hasPriorityConflict = conflictingRules.length > 0

  return (
    <form onSubmit={onSubmit} className="flex flex-col h-full bg-background">
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <div className="space-y-4 px-2">
          {/* Overview Section */}
          <div>
            <SectionHeader title="Overview" />
            <Card>
              <CardContent className="px-2 space-y-4">
                {/* Rule Name */}
                <div className="flex flex-col gap-1 pl-5 mb-4">
                  <div className="flex items-center gap-3 h-9">
                    <Label htmlFor="rule_name" className="text-sm whitespace-nowrap w-20">Rule Name</Label>
                    <Input
                      id="rule_name"
                      value={formData.rule_name}
                      onChange={(e) => onChange({ ...formData, rule_name: e.target.value })}
                      placeholder="e.g., Family events → Blue"
                      required
                      className="shadow-none flex-1"
                    />
                  </div>
                  {/* AI Notes - Show below rule name for inactive AI-generated rules */}
                  {showAiNotes && (
                    <div className="ml-[92px] text-xs text-muted-foreground leading-relaxed">
                      {currentRule.ai_notes}
                    </div>
                  )}
                </div>

                {/* Priority */}
                <div className="flex items-center gap-3 pl-5 mb-4 h-9">
                  <Label htmlFor="priority" className="text-sm whitespace-nowrap w-20">Priority</Label>
                  <Input
                    id="priority"
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={formData.priority}
                    onChange={(e) => {
                      const val = Math.max(0, Math.min(100, parseInt(e.target.value) || 0))
                      onChange({ ...formData, priority: val })
                    }}
                    className="shadow-none w-20"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* If Section */}
          <div>
            <SectionHeader title="If" />
            <Card>
              <CardContent className="px-2 pl-5">
                {metadataLoading || Object.keys(fieldMetadata).length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader size={20} className="text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading conditions...</span>
                  </div>
                ) : (
                  <ConditionsBuilder
                    conditions={formData.conditions}
                    onChange={(conditions) => onChange({ ...formData, conditions })}
                    fieldMetadata={fieldMetadata}
                    operatorMetadata={operatorMetadata}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Then Section */}
          <div>
            <SectionHeader title="Then" />
            <Card>
              <CardContent className="px-2 pl-5">
                <div className="flex flex-col md:flex-row items-start gap-2 md:gap-2">
                  {/* Action Label */}
                  <div className="w-20 md:w-20 flex-shrink-0">
                    <span className="text-sm font-medium whitespace-nowrap h-9 flex items-center">Action</span>
                  </div>

                  {/* Action Type Dropdown (loaded from automation_action_types table) */}
                  <Select
                    value={formData.action.type}
                    onValueChange={(value) => {
                      const schema = actionTypeMetadata[value]?.valueSchema
                      const defaultValue = getDefaultValueFromSchema(schema)
                      onChange({
                        ...formData,
                        action: { type: value, value: defaultValue },
                      });
                    }}
                    disabled={Object.keys(actionTypeMetadata).length <= 1}
                  >
                    <SelectTrigger className="shadow-none h-9 flex-1 md:flex-initial md:w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(actionTypeMetadata).map((actionType) => (
                        <SelectItem key={actionType.id} value={actionType.id}>
                          {actionType.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Value input: rendered based on action type's value_schema */}
                  {/* For update_color: render color picker */}
                  {actionTypeMetadata[formData.action.type]?.valueSchema?.source === 'google_calendar_colors' && (
                  <div className="flex-1 w-full min-w-0">
                    <Select
                      value={formData.action.value as string}
                      onValueChange={(value) =>
                        onChange({
                          ...formData,
                          action: { type: formData.action.type, value: value as GoogleCalendarColorId },
                        })
                      }
                    >
                      <SelectTrigger className="shadow-none h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(GOOGLE_CALENDAR_COLORS).map(([id, color]) => (
                          <SelectItem key={id} value={id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded-full border border-border"
                                style={{ backgroundColor: color.hex }}
                              />
                              {color.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  )}

                  {/* For form-based action types (e.g. add_attendee): render fields from value_schema */}
                  {actionTypeMetadata[formData.action.type]?.valueSchema?.type === 'form' &&
                    actionTypeMetadata[formData.action.type]?.valueSchema?.fields && (
                  <div className="flex flex-col gap-3 flex-1 w-full min-w-0">
                    {actionTypeMetadata[formData.action.type].valueSchema.fields!.map((field) => {
                      const currentValue = (formData.action.value ?? {}) as Record<string, unknown>;
                      if (field.type === 'text') {
                        return (
                          <Input
                            key={field.name}
                            placeholder={field.placeholder || field.label || field.name}
                            value={(currentValue[field.name] as string) ?? ''}
                            onChange={(e) =>
                              onChange({
                                ...formData,
                                action: {
                                  type: formData.action.type,
                                  value: { ...currentValue, [field.name]: e.target.value },
                                },
                              })
                            }
                            className="shadow-none h-9"
                          />
                        );
                      }
                      if (field.type === 'switch') {
                        return (
                          <div key={field.name} className="flex items-center gap-2 h-9">
                            <Switch
                              id={`action-${field.name}`}
                              checked={!!currentValue[field.name]}
                              onCheckedChange={(checked) =>
                                onChange({
                                  ...formData,
                                  action: {
                                    type: formData.action.type,
                                    value: { ...currentValue, [field.name]: checked },
                                  },
                                })
                              }
                            />
                            <Label htmlFor={`action-${field.name}`} className="text-sm cursor-pointer">
                              {field.label || field.name}
                            </Label>
                          </div>
                        );
                      }
                      if (field.type === 'select' && field.options) {
                        return (
                          <div key={field.name}>
                            {field.label && !field.hideLabel && !(formData.action.type === 'add_buffer' && field.name === 'position') && (
                              <Label className="text-xs text-muted-foreground mb-1 block">{field.label}</Label>
                            )}
                            <Select
                              value={(currentValue[field.name] as string) ?? ''}
                              onValueChange={(val) =>
                                onChange({
                                  ...formData,
                                  action: {
                                    type: formData.action.type,
                                    value: { ...currentValue, [field.name]: val },
                                  },
                                })
                              }
                            >
                              <SelectTrigger className="shadow-none h-9">
                                <SelectValue placeholder={field.label || field.name} />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options.map((opt) => {
                                  const optValue = typeof opt === 'string' ? opt : opt.value;
                                  const optLabel = typeof opt === 'string' ? opt : opt.label;
                                  return (
                                    <SelectItem key={optValue} value={optValue}>
                                      {optLabel}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      }
                      if (field.type === 'number') {
                        return (
                          <div key={field.name}>
                            {field.label && <Label className="text-xs text-muted-foreground mb-1 block">{field.label}</Label>}
                            <Input
                              type="number"
                              min={field.min}
                              max={field.max}
                              step={field.step}
                              placeholder={field.placeholder || field.label || field.name}
                              value={(currentValue[field.name] as number) ?? ''}
                              onChange={(e) =>
                                onChange({
                                  ...formData,
                                  action: {
                                    type: formData.action.type,
                                    value: { ...currentValue, [field.name]: parseInt(e.target.value) || 0 },
                                  },
                                })
                              }
                              className="shadow-none h-9"
                            />
                          </div>
                        );
                      }
                      if (field.type === 'color' && field.source === 'google_calendar_colors') {
                        return (
                          <div key={field.name}>
                            {field.label && <Label className="text-xs text-muted-foreground mb-1 block">{field.label}</Label>}
                            <Select
                              value={(currentValue[field.name] as string) ?? ''}
                              onValueChange={(val) =>
                                onChange({
                                  ...formData,
                                  action: {
                                    type: formData.action.type,
                                    value: { ...currentValue, [field.name]: val },
                                  },
                                })
                              }
                            >
                              <SelectTrigger className="shadow-none h-9">
                                <SelectValue placeholder="Select color" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(GOOGLE_CALENDAR_COLORS).map(([id, color]) => (
                                  <SelectItem key={id} value={id}>
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-4 h-4 rounded-full border border-border"
                                        style={{ backgroundColor: color.hex }}
                                      />
                                      {color.name}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                  )}
                </div>

              </CardContent>
            </Card>
          </div>

        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 py-4 flex-shrink-0 space-y-3">
        {/* Priority Conflict Warning */}
        {hasPriorityConflict && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {conflictingRules.length === 1 
                ? `Another rule "${conflictingRules[0].rule_name}" has the same priority (${formData.priority}) for this calendar.`
                : `${conflictingRules.length} other rules have the same priority (${formData.priority}) for this calendar.`
              }
            </p>
          </div>
        )}

        {/* For NEW rules: Only show Preview Impact button (mandatory preview) */}
        {!isEditing && onPreview && (
          <Button
            type="button"
            onClick={onPreview}
            disabled={previewLoading || saving}
            className="w-full"
          >
            {previewLoading ? (
              <>
                <Loader size={16} className="mr-2" />
                Generating Preview...
              </>
            ) : (
              'Preview Impact'
            )}
          </Button>
        )}

        {/* For EDITING: Show 4 action buttons in a grid */}
        {isEditing && (
          <div className="grid grid-cols-4 gap-2">
            {/* Delete Button */}
            <Button
              type="button"
              variant="outline"
              onClick={onDelete}
              disabled={saving || !onDelete}
              className="flex flex-col items-center justify-center gap-1 h-auto py-3 border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
            >
              <TrashIcon className="h-5 w-5" />
              <span className="text-xs">Delete</span>
            </Button>

            {/* Pause/Un-pause Button */}
            <Button
              type="button"
              variant="outline"
              onClick={onPause || onReactivate}
              disabled={saving || (!onPause && !onReactivate)}
              className="flex flex-col items-center justify-center gap-1 h-auto py-3 disabled:opacity-50"
            >
              {onPause ? (
                <>
                  <PauseIcon className="h-5 w-5" />
                  <span className="text-xs">Pause</span>
                </>
              ) : (
                <>
                  <PlayIcon className="h-5 w-5" />
                  <span className="text-xs">Un-pause</span>
                </>
              )}
            </Button>

            {/* Preview Button */}
            <Button
              type="button"
              variant="outline"
              onClick={onPreview}
              disabled={previewLoading || saving || !onPreview}
              className="flex flex-col items-center justify-center gap-1 h-auto py-3 disabled:opacity-50"
            >
              {previewLoading ? (
                <>
                  <Loader size={20} />
                  <span className="text-xs">Loading...</span>
                </>
              ) : (
                <>
                  <PreviewIcon className="h-5 w-5" />
                  <span className="text-xs">Preview</span>
                </>
              )}
            </Button>

            {/* Save Button */}
            <Button
              type="submit"
              disabled={saving || !formData.is_active}
              className="flex flex-col items-center justify-center gap-1 h-auto py-3 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader size={20} />
                  <span className="text-xs">Saving...</span>
                </>
              ) : (
                <>
                  <Check className="h-5 w-5" />
                  <span className="text-xs">Save</span>
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </form>
  )
}

// Filter Popover Content Component
interface FilterPopoverContentProps {
  selectedActionTypeFilters: string[]
  onActionTypeFilterChange: (typeIds: string[]) => void
  onClearFilters: () => void
  hasActiveFilters: boolean
  actionTypeMetadata: Record<string, ActionTypeMetadata>
}

function FilterPopoverContent({
  selectedActionTypeFilters,
  onActionTypeFilterChange,
  onClearFilters,
  hasActiveFilters,
  actionTypeMetadata,
}: FilterPopoverContentProps) {
  const handleActionTypeToggle = (typeId: string) => {
    if (selectedActionTypeFilters.includes(typeId)) {
      onActionTypeFilterChange(selectedActionTypeFilters.filter(id => id !== typeId))
    } else {
      onActionTypeFilterChange([...selectedActionTypeFilters, typeId])
    }
  }

  const sortedActionTypes = Object.values(actionTypeMetadata)
    .filter(at => at.isEnabled)
    .sort((a, b) => a.displayName.localeCompare(b.displayName))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="h-7 text-xs"
          >
            Clear all
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground">Action</Label>
        <div className="space-y-1.5">
          {sortedActionTypes.map((actionType) => {
            const isSelected = selectedActionTypeFilters.includes(actionType.id)
            return (
              <label
                key={actionType.id}
                className={cn(
                  "flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition-colors hover:bg-accent",
                  isSelected && "bg-accent"
                )}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleActionTypeToggle(actionType.id)}
                />
                <span className="text-xs">{actionType.displayName}</span>
              </label>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Normalizes conditions loaded from DB to ensure each condition uses the
 * unified values:string[] format. Handles legacy data that stored value as
 * a single string and/or included valueType.
 */
function normalizeConditionsFromDB(conditions: ConditionsGroup): ConditionsGroup {
  const MULTI_VALUE_OPERATORS = ['contains_any', 'contains_all']

  return {
    ...conditions,
    conditions: conditions.conditions.map(condition => {
      // DB data may contain legacy fields (value, valueType) not on the current Condition type
      const raw = condition as unknown as Record<string, unknown>

      // Already normalized
      if (Array.isArray(condition.values) && condition.values.length > 0 && !('value' in raw)) {
        const { field, operator, values } = condition
        return { field, operator, values }
      }

      // Legacy: value exists but values doesn't
      const legacyValue = raw.value
      if (legacyValue != null && (!Array.isArray(condition.values) || condition.values.length === 0)) {
        const strVal = String(legacyValue)
        const isMulti = MULTI_VALUE_OPERATORS.includes(condition.operator)
        const values = isMulti
          ? strVal.split(',').map(v => v.trim()).filter(v => v.length > 0)
          : [strVal]
        return { field: condition.field, operator: condition.operator, values }
      }

      return { field: condition.field, operator: condition.operator, values: condition.values || [] }
    }),
  }
}

function getDefaultFormData(): RuleFormData {
  return {
    rule_name: '',
    conditions: {
      operator: 'AND',
      conditions: [
        {
          field: 'title',
          operator: 'contains',
          values: [],
        },
      ],
    },
    action: {
      type: 'update_color',
      value: '5',
    },
    calendar_id: null,
    priority: 0,
    is_active: true,
  }
}

